import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { object, mixed, isSchema } from 'yup';

import { makePath, makeKey } from '../utils';

function _default() {
  var _this = this;

  if (!this._nodes.length) return undefined;
  var dft = {};

  this._nodes.forEach(function (key) {
    const value = _this.fields[key]?.default();

    if (value !== undefined) {
      dft[key] = value;
    }
  });

  return dft;
}

export class ObjectSchemaType extends object {
  constructor(spec, namespace) {
    super(spec);
    this._defaultDefault = _default;
    this._namespace = namespace;
    this._stripEmpty = false;
  }

  namespace(namespace) {
    if (namespace) {
      const next = this.clone();
      next._namespace = namespace;
      return next;
    }
    return this._namespace;
  }

  _isEqual(value, otherValue) {
    const refFields = this._nodes.filter(
      (node) => !isSchema(this.fields[node])
    );
    if (refFields.length) {
      value = omit(value, refFields);
    }

    return isEqual(value, otherValue);
  }

  _cast(_value, { applyConst, parseConst, ...options } = {}) {
    const __value = _value === null ? undefined : _value;
    let value = super._cast(__value, { ...options, nested: true }) || {};

    if (options.nested) {
      let intermediateValue = { ...value };
      Object.keys(value).forEach((node) => {
        if (value.hasOwnProperty(node) && value[node] === undefined) {
          delete intermediateValue[node];
        }
      });

      value = intermediateValue;
    }

    if (this._stripNullable) {
      let intermediateValue = { ...value };
      Object.keys(value).forEach((node) => {
        if (value[node] === null) {
          delete intermediateValue[node];
        }
      });
      value = intermediateValue;
    }

    if (applyConst) {
      value = this.applyConst(value);
    } else if (parseConst) {
      value = this.parseConst(value);
    }

    if (this._stripEmpty && !Object.keys(value).length) {
      value = null;
    }

    return value;
  }

  shapeDefaults(defaults) {
    return this.default({
      ...this.default(),
      ...defaults,
    });
  }

  applyConst(_value) {
    return this._nodes.reduce((value, node) => {
      const field = this.fields[node];
      if (_value.hasOwnProperty(node) && isSchema(field)) {
        value[node] = field.applyConst(_value[node]);
      }
      return value;
    }, {});
  }

  parseConst(_value) {
    return this._nodes.reduce((value, node) => {
      const field = this.fields[node];
      if (_value && _value.hasOwnProperty(node) && isSchema(field)) {
        value[node] = field.parseConst(_value[node]);
      }
      return value;
    }, {});
  }

  stripEmpty() {
    const next = this.nullable();
    next._stripEmpty = true;
    next._stripNullable = true;
    return next;
  }

  stripDefaults({ stripEmpty = true } = {}) {
    let next = mixed.prototype.stripDefaults.call(this);

    next._stripEmpty = stripEmpty;
    next._stripNullable = true;
    next.fields = this._nodes.reduce((fields, node) => {
      const field = this.fields[node];

      if (!isSchema(field)) {
        // skip ref field
        return fields;
      }

      return {
        ...fields,
        [node]: field.stripDefaults(),
      };
    }, {});

    return next;
  }

  traverse(callback, options = {}) {
    this._nodes.forEach((node) => {
      const field = this.fields[node];
      const innerOptions = {
        path: makePath`${options.path}.${node}`,
        namespace: makeKey`${options.namespace}_${this._namespace}`,
        parent: node,
      };

      if (isSchema(field)) {
        field.traverse(callback, innerOptions);
      }
    });
  }

  encode(_value, options = {}) {
    const encodedValue = {};
    const value = this.cast(_value, {
      applyConst: true,
      context: {},
      ...options,
    });

    this.traverse(({ field, key, getter }) => {
      const fieldValue = getter(value);
      if (fieldValue !== undefined && fieldValue !== null) {
        encodedValue[key] = field.stringify(fieldValue, options, key);
      }
    }, options);

    return encodedValue;
  }

  decode(_value, options = {}) {
    const decodedValue = {};
    this.traverse(({ field, key, setter }) => {
      if (_value.hasOwnProperty(key)) {
        const parsedValue = field.parse(_value[key]);
        if (parsedValue !== undefined) {
          setter(decodedValue, parsedValue, options, key);
        }
      }
    }, options);

    const value = this.cast(decodedValue, {
      parseConst: true,
      context: {},
      ...options,
    });

    return value;
  }
}

export default (spec, namespace) => {
  return new ObjectSchemaType(spec, namespace);
};
